<template>
    <component :is="props.embedded ? 'div' : VxPageLayout">
        <page-header v-if="props.embedded">
            <slot name="title">Imports</slot>
            <template v-slot:actions>
                <router-button v-if="!props.readOnly && props.newImportHref" size="lg" :to="props.newImportHref">
                    New Import <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </router-button>
            </template>
        </page-header>

        <template v-if="!props.embedded" v-slot:title>
            <slot name="title">Imports</slot>
        </template>

        <template v-if="!props.embedded" v-slot:actions>
            <vx-button
                v-if="$slots.overview"
                @click="showOverview = !showOverview"
                data-test="toggle-overview"
                :aria-label="`${showOverview ? 'Hide' : 'Show'} Overview`"
                type="button"
                color="muted-dark"
                size="lg"
            >
                <span class="hidden sm:inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                <font-awesome-icon
                    widthAuto
                    class="hidden! sm:inline-block!"
                    :icon="faCircleQuestion"
                ></font-awesome-icon>
                <font-awesome-icon
                    widthAuto
                    class="inline-block! sm:hidden!"
                    :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"
                ></font-awesome-icon>
            </vx-button>
            <router-button v-else-if="!props.readOnly && props.newImportHref" size="lg" :to="props.newImportHref">
                New Import <font-awesome-icon widthAuto :icon="faCirclePlus" />
            </router-button>
        </template>

        <div v-if="$slots.notice">
            <slot name="notice"></slot>
        </div>

        <div v-if="$slots.overview">
            <slot name="overview"></slot>
        </div>

        <page-controls v-if="$slots.overview && list.length" class="items-center">
            <template v-slot:left>
                <vx-alert v-if="recentlyCreated" color="success" :closeable="false" shadow>
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                    </template>
                    <strong>New Import</strong> started successfully
                </vx-alert>
            </template>

            <template v-slot:right>
                <router-button v-if="!props.readOnly && props.newImportHref" size="lg" block :to="props.newImportHref">
                    New Import <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </router-button>
            </template>
        </page-controls>
        <vx-alert v-else-if="recentlyCreated" color="success" :closeable="false" shadow>
            <template v-slot:icon>
                <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
            </template>
            <strong>New Import</strong> started successfully
        </vx-alert>

        <page-loading v-if="loading && !list.length"></page-loading>

        <vx-box
            v-if="!loading && !list.length"
            color="info"
            class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
        >
            No imports yet.
            <router-button v-if="!props.readOnly && props.newImportHref" :to="props.newImportHref">
                New Import <font-awesome-icon widthAuto :icon="faCirclePlus" />
            </router-button>
        </vx-box>

        <vx-table v-if="list.length" class="lg:grid-cols-7-auto mt-4" rowBreakpoint="lg">
            <vx-table-row isHeader>
                <div>Status</div>
                <div class="xl:col-span-2">Name</div>
                <div>Rows</div>
                <div>Errors</div>
                <div>Created at</div>
            </vx-table-row>

            <import-row
                v-for="(importItem, index) in list"
                :modelValue="list[index]"
                :key="list[index].import_id"
                class="min-h-[74px]"
            ></import-row>
        </vx-table>

        <div v-if="fetchParams.cursor" class="mt-8">
            <vx-button
                @click="loadData()"
                color="muted-dark"
                size="lg"
                class="grow-0"
                data-test="load-more-btn"
                :loading="loading"
            >
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>
        </div>
    </component>
</template>

<script setup>
import {
    faCircleCheck,
    faCircleChevronDown,
    faCircleChevronUp,
    faCirclePlus,
    faCircleQuestion,
    faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { VxAlert, VxBox, VxButton, VxPageLayout, VxTable, VxTableRow } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, onMounted } from 'vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { importsClient, groupImportsClient } from '~/services';
import PageLoading from '~/components/general/PageLoading.vue';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import { useQueryParams } from '~/composables/useQueryString';
import ImportRow from '~/components/import/ImportRow.vue';
import PageControls from '~/components/general/PageControls.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import RouterButton from '~/components/general/RouterButton.vue';

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const props = defineProps({
    context: {
        type: Object,
        required: true,
    },
    importType: {
        type: String,
        required: true,
        validator: (type) => ['contact', 'user', 'conversion', 'group'].includes(type),
    },
    embedded: {
        type: Boolean,
        default: false,
    },
    newImportHref: {
        type: String,
        required: false,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
});

const showOverview = defineModel('showOverview', {
    type: Boolean,
    default: false,
});

const defaultFilters = () => ({
    cursor: null,
    forward: false,
});

const queryParams = useQueryParams();

onMounted(async () => {
    fetchParams.value = defaultFilters();
    await loadData();

    if (queryParams.success && queryParams.import_id && queryParams.file_name) {
        if (!list.value.find((item) => item.import_id === queryParams.import_id)) {
            list.value.unshift({
                object: 'import',
                import_type: props.importType,
                import_id: queryParams.import_id,
                file_name: queryParams.file_name,
                created_at: toUTCDateTimeFormat(dayjs().utc()),
            });

            list.value = list.value.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        }

        queryParams.success = undefined;
        queryParams.import_id = undefined;
        queryParams.file_name = undefined;
    }
});

const recentlyCreated = computed(() =>
    list.value[0]?.created_at
        ? dayjs.utc(list.value[0].created_at).isAfter(dayjs().utc().subtract(15, 'seconds'))
        : false
);

const loadData = () => {
    const request = !props.context.groupId
        ? importsClient.list(props.context.teamId, props.importType, { ...fetchParams.value })
        : groupImportsClient.list(props.context.teamId, props.context.groupId, props.importType, {
              ...fetchParams.value,
          });

    return fetchRecords(request);
};
</script>
